import React, { useState, useEffect } from "react";
import { Modal, TextField, Button, BlockStack, Text, Spinner, InlineStack } from "@shopify/polaris";
import { SearchIcon } from "@shopify/polaris-icons";
import moment from "moment";
import supabase from "../../supabase-client";
import CarCard from "./CarCard";

function ChangeCarModal({
  open,
  onClose,
  dateRange,
  currentCarId,
  branchId,
  onSelect,
  onSublease,
  isCurrentlySublease = false,
}) {
  const [search, setSearch] = useState("");
  const [cars, setCars] = useState(null);
  const [selectedCar, setSelectedCar] = useState(null);
  const [loading, setLoading] = useState(false);
  const [changeDate, setChangeDate] = useState(moment().format("YYYY-MM-DD"));

  // Reset change date to today each time the modal opens
  useEffect(() => {
    if (open) {
      setChangeDate(moment().format("YYYY-MM-DD"));
      setCars(null);
      setSelectedCar(null);
      setSearch("");
    }
  }, [open]);

  const fetchCars = async () => {
    if (!dateRange) return;
    setLoading(true);

    // Reservation end (upper bound of the existing range)
    const clean = dateRange.replace(/[\[\]()]/g, "");
    const parts = clean.split(",");
    const toDate = parts[1]?.replace(/"/g, "").trim();
    const effectiveTo = toDate || "2099-12-31 23:59";

    // From = user-picked change date at 00:00, not the reservation's start.
    // This allows swapping to a car that was occupied earlier in the reservation.
    const fromDate = `${changeDate} 00:00:00`;

    const { data, error } = await supabase
      .rpc("get_available_cars", {
        from_date: fromDate,
        to_date: effectiveTo,
        category_id: 0,
        search_text: search.trim() || "",
        // Only cars in the reservation's branch: the DB refuses the swap otherwise.
        p_branch_id: branchId || null,
      })
      .select()
      .range(0, 19);

    if (error) {
      console.error(error.message);
      setLoading(false);
      return;
    }

    setCars(data);
    setLoading(false);
  };

  const handleConfirm = () => {
    if (selectedCar) {
      // Pass the change date alongside the car id so the caller can pre-fill it
      onSelect(selectedCar, { changeDate });
      onClose();
      setCars(null);
      setSelectedCar(null);
      setSearch("");
    }
  };

  const handleClose = () => {
    onClose();
    setCars(null);
    setSelectedCar(null);
    setSearch("");
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Cambiar Auto"
      primaryAction={{
        content: "Confirmar",
        onAction: handleConfirm,
        disabled: !selectedCar,
      }}
      secondaryActions={[
        ...(onSublease
          ? [{
              content: isCurrentlySublease ? "Editar Subarrendado" : "Subarrendar",
              onAction: () => {
                handleClose();
                onSublease();
              },
            }]
          : []),
        { content: "Cancelar", onAction: handleClose },
      ]}
      large
    >
      <Modal.Section>
        <BlockStack gap="300">
          <InlineStack gap="200" blockAlign="end">
            <div style={{ width: 180 }}>
              <TextField
                label="Fecha del cambio"
                type="date"
                value={changeDate}
                onChange={setChangeDate}
                autoComplete="off"
              />
            </div>
            <Text tone="subdued" variant="bodySm">
              Se listan autos disponibles desde esta fecha hasta el final de la reserva.
            </Text>
          </InlineStack>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              fetchCars();
            }}
          >
            <div style={{ display: "flex", gap: "0.25rem" }}>
              <span style={{ flexGrow: 1 }}>
                <TextField
                  autoComplete="off"
                  placeholder="Buscar por placa, modelo o marca"
                  value={search}
                  onChange={setSearch}
                  label="Buscar vehículo disponible"
                  labelHidden
                />
              </span>
              <Button icon={SearchIcon} submit loading={loading} />
            </div>
          </form>

          {loading ? (
            <div style={{ textAlign: "center", padding: "2rem" }}>
              <Spinner size="small" />
            </div>
          ) : cars === null ? (
            <Text tone="subdued">Busca para ver autos disponibles</Text>
          ) : cars.length === 0 ? (
            <Text tone="subdued">No se encontraron autos disponibles</Text>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: "0.5rem",
                maxHeight: "400px",
                overflowY: "auto",
              }}
            >
              {cars
                .filter((c) => c.id !== currentCarId)
                .map((car) => (
                  <CarCard
                    key={car.id}
                    car={car}
                    selected={selectedCar}
                    onClick={({ car: carId, model }) => setSelectedCar(carId)}
                  />
                ))}
            </div>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

export default ChangeCarModal;
